import { FunctionalComponent } from 'preact';
import { Suspense, lazy } from 'preact/compat';
import {
  useRef,
  useLayoutEffect,
  useEffect,
  useCallback,
} from 'preact/hooks';
import { useSelector, useDispatch } from 'react-redux';
import cn from 'classnames';

import {
  // createFormatOrganic,
  // createOrganicSuggestion,
  // createOrganicUrl,
  getFaviconUrl,
} from './slice/selectors/suggestions/utils';
import { ChangeEvent } from '../../types.global';
import { getTranslation, sendMessage } from '../../../shared/chromeApi';
import { selectIsUserRegular } from '../../store/selectors';
import { selectFeatureFlags } from '../../store/selectors';
import { hideOnboardingStep, selectOnboardingStep } from '../user/slice';
import { getCommands } from '../../../shared/chromeApi';
import ErrorBoundary from '../../ErrorBoundary';
import {
  speechStarted,
  suggestionHover,
  pressedEscape,
  toggledChooseEngine,
  discoveredCommand,
  // chooseEngine,
} from './slice/actions';
import {
  searchSubmitted,
  changedInput,
  keyDown,
  speechEnded,
  selectHighlighted,
  removeSuggestion,
  selectIsAutofocus,
  selectSuggestions,
  selectInstantResults,
  selectInstantResultsHighlighted,
  selectIsFocus,
  toggledFocus,
  selectIsChooseEngineOpened,
  selectCommand,
  selectRendered,
  // searchTagSelect,
  selectFetchedQuery,
  fetchEntities,
  selectEngine,
} from './slice';

import { setOhCookies } from '../../../shared/cookie';
import { timeInMs } from '../../../shared';

import { ENGINES } from './utils';

import styles from './styles.module.scss';
import SuggestionElement from './components/SuggestionElement';
import SuggestionPlaceholder from './components/SuggestionPlaceholder';
import SearchIconArea from './components/SearchIcon';
import Shortcut from './components/Shortcut';
// import SearchTags from './components/SearchTags';


import { GlobalState } from '../../types.global';
import {
  // selectDisplayedSearchTags,
  selectChoosenSearchTag,
} from './slice/selectors/searchTagsSelctors';
import LinkedTabs from './components/LinkedTabs';
// import { selectActiveHint } from './slice/selectors/hints';
// import { Suggestion } from './slice/types';

const VoiceSearch = lazy(() => import('./components/VoiceSearch'));
// const ChooseEnginePopover = lazy(
//   () => import('./components/ChooseEnginePopover')
// );
const InstantAnswer = lazy(() => import('./components/InstantAnswer'));
const InstantAnswerSidebar = lazy(
  () => import('./components/InstantAnswerSidebar')
);
// const Hint = lazy(() => import('./components/Hint'));

const EMPTY_LIST = [];

const SearchForm: FunctionalComponent = () => {
  const dispatch = useDispatch();

  const suggestionRefs = useRef<Record<string, HTMLElement>>({});

  const handleSuggestionRef = useCallback((id, element: HTMLElement) => {
    if (element) {
      suggestionRefs.current[id] = element;
    } else {
      delete suggestionRefs.current[id];
    }
  }, []);

  const isChooseEngineOpened = useSelector(selectIsChooseEngineOpened);
  const engine = useSelector(selectEngine);
  const isRegular = useSelector(selectIsUserRegular);
  const highlighted = useSelector(selectHighlighted);
  const isAutofocus = useSelector(selectIsAutofocus);
  const refIsAutofocused = useRef(isAutofocus);

  const instantResults = useSelector(selectInstantResults);
  const instantResultsHighlighted = useSelector(
    selectInstantResultsHighlighted
  );

  const isFocused = useSelector(selectIsFocus);
  const list = useSelector((state: GlobalState) =>
    isFocused ? selectSuggestions(state) : EMPTY_LIST
  );

  // console.log('list', list);

  const command = useSelector(selectCommand);
  const onboardingStep = useSelector(selectOnboardingStep);
  // const activeHint = useSelector(selectActiveHint);
  const rendered = useSelector(selectRendered);
  const fetched = useSelector(selectFetchedQuery);

  const {
    // CUSTOM_SEARCH_ENGINE,
    MAGIC_SEARCH_BOX,
    SEARCH_BOX_SHORTCUT,
    SEARCH_BOX_FOCUS_HISTORY,
    // SEARCH_TAGS,
    AD_WHALE,
  } = useSelector(selectFeatureFlags);

  const searchState = useSelector((global: GlobalState) => global.search);

  useEffect(() => {
    setOhCookies('OH_IS_REGULAR', isRegular, {
      expirationDate: Math.round((Date.now() + timeInMs.day * 7) / 1000),
    });
  }, [isRegular]);

  const inputRef = useRef<HTMLInputElement>(null);
  const $form = useRef<HTMLFormElement>(null);
  const { url, q } = ENGINES[engine] || {};

  useLayoutEffect(() => {
    const startsAt = rendered
      .toLowerCase()
      .startsWith(searchState.provided.toLowerCase());

    const { current } = inputRef;
    const { selectionStart, selectionEnd } = current;

    if (
      startsAt &&
      selectionStart === selectionEnd &&
      current.value?.length === selectionStart
    ) {
      current.setSelectionRange(searchState.provided.length, rendered.length);
    }
  }, [searchState.provided, rendered]);

  const choosenSearchTag = useSelector(selectChoosenSearchTag);
  // const searchTags = useSelector(selectDisplayedSearchTags);

  // const handleSearchTagClick = useCallback((searchTag) => {
  //   dispatch(searchTagSelect(searchTag.u));
  //   inputRef.current.focus();
  // }, []);

  useEffect(() => {
    sendMessage({ type: 'setNtpSearchBoxFocus', payload: isAutofocus }).catch(
      console.error
    );
  }, [isAutofocus]);

  useEffect(() => {
    const queryFocus = new URLSearchParams(window.location.search).get('focus');
    if (queryFocus || isAutofocus || isFocused || searchState.provided) {
      inputRef.current.focus();

      // if (!engine && queryFocus) {
      //   dispatch(toggledChooseEngine(true));
      // }

      // else {
      //   if ((SEARCH_BOX_FOCUS_HISTORY && queryFocus) || searchState.provided) {
      //     dispatch(updatedInput(searchState.provided, isFocused ? 'goBack' : 'autofocus'));
      //   }
      // }
    }

    getCommands().then((list) => {
      const command = list.find(
        ({ name, shortcut }) => name === 'open-tab' && shortcut
      );
      dispatch(discoveredCommand(command));
    });

    dispatch(fetchEntities());
  }, []);

  const handleRemoveSuggestion = useCallback((item) => {
    inputRef.current.focus();
    dispatch(removeSuggestion(item));
  }, []);

  const handleClickSuggestion = useCallback((item, event) => {
    inputRef.current.focus();
    dispatch(searchSubmitted(event, item));
  }, []);

  // const toggleChooseEngine = useCallback(
  //   (e: MouseEvent | KeyboardEvent): void => {
  //     e.preventDefault();

  //     dispatch(toggledChooseEngine());
  //     if (onboardingStep === 'searchForm') {
  //       dispatch(hideOnboardingStep({ step: onboardingStep }));
  //     }
  //   },
  //   [onboardingStep]
  // );

  const handleFocus = useCallback(() => {
    if (refIsAutofocused.current) {
      refIsAutofocused.current = false;
      return;
    }

    dispatch(toggledFocus(true));
  }, []);

  const handleVoiceClick = useCallback(() => {
    dispatch(engine ? speechStarted() : toggledChooseEngine(true));
  }, []);

  const isInteractive =
    !isChooseEngineOpened &&
    isFocused &&
    (SEARCH_BOX_FOCUS_HISTORY || rendered || choosenSearchTag);

  const placeholder = (() => {
    if (choosenSearchTag) {
      return `Search in ${choosenSearchTag.s}`;
    }
    return typeof window === 'undefined'
      ? 'Search'
      : getTranslation(`search_input${engine ? `_${engine}` : ''}`);
  })();

  const handleTyping = useCallback(
    (evt: ChangeEvent) => dispatch(changedInput(evt)),
    []
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (onboardingStep === 'searchForm') {
        dispatch(hideOnboardingStep({ step: onboardingStep }));
      }
      dispatch(keyDown(event));
    },
    [onboardingStep]
  );

  const handleMouseEnter = useCallback((id: string) => {
    dispatch(suggestionHover(id));
  }, []);

  return (
    <>
      {searchState.isSpeech && (
        <Suspense fallback={null}>
          <VoiceSearch onClose={(content) => dispatch(speechEnded(content))} />
        </Suspense>
      )}

      <div
        className={cn(styles.container, isInteractive && styles.withContent)}
      >
{/* {SEARCH_TAGS && (
          <SearchTags
            isActive={!!isInteractive}
            onClick={handleSearchTagClick}
            searchTags={searchTags}
            choosen={choosenSearchTag}
          />
        )} */}

        <LinkedTabs />

        <form
          className={cn(
            styles.searchForm,
            // isPressAgainActive && styles.isPreselected,
            'searchFormTC'
          )}
          action={url}
          method="GET"
          ref={$form}
        >
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSeb1EFRZ0zzjmvc_V9sX1R9r7vfnKSHErBtbfOX9ObcJhZamw/viewform?usp=sf_link"
            className={cn(
              styles.feedbackLink,
              // SEARCH_TAGS && styles.withSearchTags,
              styles.withSearchTags,
              MAGIC_SEARCH_BOX && isFocused ? styles.isDisabled : ''
            )}
          >
            Give us feedback
          </a>

          <div
            className={cn(
              styles.innerWrapper,
              list.length && isInteractive && styles.isWithAutosuggestions,
              onboardingStep === 'searchForm' && styles.outline
            )}
          >
            <div className={styles.searchBox}>
              <SearchIconArea
                icon={
                  highlighted?.favIconUrl ||
                  (choosenSearchTag ? getFaviconUrl(choosenSearchTag.u) : null)
                }
                isSubmitted={searchState.isSubmitted}
                // onClick={CUSTOM_SEARCH_ENGINE ? toggleChooseEngine : null}
                onClick={null}
                engine={engine}
              />

              {/* {isChooseEngineOpened && (
                <Suspense fallback={null}>
                  <ChooseEnginePopover
                    onClose={toggleChooseEngine}
                    handleChooseEngine={handleChooseEngine}
                    engine={engine}
                  />
                </Suspense>
              )} */}

              <input
                className={styles.input}
                ref={inputRef}
                placeholder={placeholder}
                autoComplete="off"
                autoCorrect="off"
                name={q}
                required
                value={rendered}
                onChange={handleTyping}
                onKeyDown={handleKeyDown}
                onFocus={handleFocus}
                onClick={handleFocus}
                // @ts-ignore
                spellCheck="false"
                id="searchInput"
                autoCapitalize="none"
              />

              {/*<div className={styles.pressAgainContainer}>*/}
              {/*  <span className={styles.label}>press enter again</span>*/}
              {/*  <button*/}
              {/*    onClick={handleSearchHighlighted}*/}
              {/*    className={styles.button}*/}
              {/*  >Search</button>*/}
              {/*</div>*/}

              {SEARCH_BOX_SHORTCUT && !isAutofocus && !isFocused && command && (
                <Suspense fallback={null}>
                  <Shortcut command={command} />
                </Suspense>
              )}

              <button
                type="button"
                className={cn(
                  styles.voiceSearchBtn
                  // process.env.APP_PLATFORM === 'web' && styles.isVoiceHidden
                )}
                onClick={handleVoiceClick}
              >
                <svg
                  width="14"
                  height="20"
                  viewBox="0 0 14 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <mask
                    id="mask0"
                    mask-type="alpha"
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="10"
                    width="14"
                    height="10"
                  >
                    <path
                      d="M12.3 10C12.3 13 9.76 15.1 7 15.1C4.24 15.1 1.7 13 1.7 10H0C0 13.41 2.72 16.23 6 16.72V20H8V16.72C11.28 16.24 14 13.42 14 10H12.3Z"
                      fill="white"
                    />
                  </mask>
                  <g mask="url(#mask0)">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M-18 -14H32V36H-18V-14Z"
                      fill="#256EF3"
                    />
                  </g>
                  <mask
                    id="mask1"
                    mask-type="alpha"
                    maskUnits="userSpaceOnUse"
                    x="4"
                    y="0"
                    width="6"
                    height="12"
                  >
                    <path
                      d="M7 12C8.66 12 9.99 10.66 9.99 9L10 3C10 1.34 8.66 0 7 0C5.34 0 4 1.34 4 3V9C4 10.66 5.34 12 7 12Z"
                      fill="white"
                    />
                  </mask>
                  <g mask="url(#mask1)">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M-18 -15H32V35H-18V-15Z"
                      fill="#6FD0A0"
                    />
                  </g>
                </svg>
              </button>
            </div>

            <div className={styles.searchSuggestions}>
              {list
                .filter(Boolean)
                .map((item, i) =>
                  item.type === 'placeholder' ? (
                    <SuggestionPlaceholder key={i} />
                  ) : (
                    <SuggestionElement
                      key={item.id}
                      displayWhale={!!AD_WHALE}
                      customRef={handleSuggestionRef}
                      suggestion={item}
                      value={fetched}
                      isActive={highlighted === item}
                      onClick={handleClickSuggestion}
                      onMouseEnter={
                        item.type === 'instantResult' ? handleMouseEnter : null
                      }
                      onRemove={handleRemoveSuggestion}
                      engine={engine}
                    />
                  )
                )}
            </div>
          </div>

          {/* {isFocused && !instantResultsHighlighted && activeHint && (
            <Suspense fallback={null}>
              <Hint text={activeHint} />
            </Suspense>
          )} */}

          {instantResultsHighlighted &&
            isFocused &&
            suggestionRefs.current[instantResultsHighlighted.id] && (
              <Suspense fallback={null}>
                <ErrorBoundary message={null} name="instantAnswer">
                  <InstantAnswer
                    q={searchState.provided}
                    data={instantResults.webPages.value.find(
                      ({ url }) => url === instantResultsHighlighted.url
                    )}
                    $form={$form.current}
                    $suggestion={
                      suggestionRefs.current[instantResultsHighlighted.id]
                    }
                  />
                </ErrorBoundary>
              </Suspense>
            )}

          {instantResults?.images && isFocused && (
            <Suspense fallback={null}>
              <ErrorBoundary message={null} name="instantAnswerSidebar">
                <InstantAnswerSidebar
                  data={instantResults}
                  $form={$form.current}
                />
              </ErrorBoundary>
            </Suspense>
          )}
        </form>
      </div>

      {isInteractive && (
        <div
          className={styles.overlay}
          onClick={() => dispatch(pressedEscape())}
        />
      )}
    </>
  );
};

export default SearchForm;
